import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import {makeHighlightStyle} from 'theme';
import stringFromNode from 'helpers/stringFromNode';
import {useMCResponse} from 'data/queries/responses';

const useStyles = makeStyles(theme => {
  const highlightStyle = makeHighlightStyle(theme);
  return {
    card: highlightStyle({
      colors: theme.palette.legacy.actionHighlight.solution,
      before: {content: `'\\e896'`}, // list e896
    }),
    title: {
      fontWeight: 700,
      marginBottom: theme.spacing(1),
      display: 'block',
    },
    option: {
      borderWidth: theme.spacing(0.25),
      borderColor: ({checked}) =>
        checked ? theme.palette.text.disabled : theme.palette.text.primary,
      '&$selected': {
        borderColor: theme.palette.secondary.main,
      },
      '&$correct': {
        borderColor: theme.palette.text.primary,
      },
      borderStyle: 'solid',
      borderRadius: 4,
      margin: theme.spacing(1),
      paddingRight: theme.spacing(1),
      marginLeft: 0,
    },
    selected: ({checked}) => ({
      '&:before': checked && {
        content: `'\\e99a'`, // dangerous e99a
        color: theme.palette.error.main,
        fontFamily: 'Lato,Material Icons',
        position: 'absolute',
        left: theme.spacing(-4),
        fontSize: 22,
        fontWeight: 900,
      },
    }),
    correct: {
      '&:before': {
        content: `'\\e86c'`, // check_circle e86c
        color: theme.palette.success.main,
        fontFamily: 'Lato,Material Icons',
        position: 'absolute',
        left: theme.spacing(-4),
        fontSize: 22,
        fontWeight: 900,
      },
    },
  };
});

const MultipleChoice = ({prompt, choices, correctIndex, answer}) => {
  const promptText = stringFromNode(prompt);
  const [_latestResponse, createResponse, _responses] = useMCResponse(promptText);
  const [value, setValue] = React.useState(null);
  const handleChange = event => setValue(event.target.value);

  const [checked, setChecked] = React.useState(false);

  const classes = useStyles({checked});
  const checkAnswer = () => {
    setChecked(true);
    const correct = value === correctIndex.toString();
    createResponse({value, correct});
  };

  return (
    <Box marginBottom={4} className={classes.card}>
      <Box mb={3}>{prompt}</Box>
      <FormControl component="fieldset">
        <RadioGroup
          aria-label="multiple-choice-question"
          name="multiple-choice-question"
          value={value}
          onChange={handleChange}
        >
          {choices.map((option, index) => (
            <FormControlLabel
              // justification: this is coming from static mdx, so will not change
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              value={index.toString()}
              disabled={checked && index !== correctIndex}
              control={<Radio />}
              label={option.props ? option.props.children : option}
              className={clsx({
                [classes.option]: true,
                [classes.selected]:
                  index.toString() === value && (!checked || index !== correctIndex),
                [classes.correct]: checked && index === correctIndex,
              })}
            />
          ))}
        </RadioGroup>
        {checked ? (
          answer && (
            <Box mt={2}>
              <Paper>
                <Box p={2}>{answer}</Box>
              </Paper>
            </Box>
          )
        ) : (
          <Box marginTop={4}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!value}
              onClick={checkAnswer}
            >
              Check Answer
            </Button>
          </Box>
        )}
      </FormControl>
    </Box>
  );
};

MultipleChoice.propTypes = {
  prompt: PropTypes.node.isRequired,
  answer: PropTypes.node,
  choices: PropTypes.arrayOf(PropTypes.node).isRequired,
  correctIndex: PropTypes.number,
};

export default MultipleChoice;
